/**
 * Shared derivations for the two submission grids (loan applications and credit
 * authorizations). Both come off `/borrower_submissions/*` and share the
 * `_SubmissionBase` shape in lendmarq-os, so the display logic lives here once.
 *
 * Note the list endpoints project out PII (`borrowerSSN`, `dateOfBirth`,
 * `signature`, the raw payload) — don't add columns for those, they will always
 * be undefined.
 */

/**
 * The borrower, however this submission happens to carry them.
 *
 * The `borrowers` array is the normalized shape, but live submissions routinely
 * arrive with it EMPTY and every borrower field inline at the top level instead
 * (verified against a 2026-08-06 credit auth: `borrowers: []`, with firstName /
 * emailAddress / citizenshipStatus / maritalStatus / primaryAddress all at the
 * root). Anything that reads `row.borrowers[0]` alone renders an em dash for a
 * borrower whose details are right there.
 *
 * Top level wins where both carry a value — it's the shape the current public
 * forms submit.
 */
const BORROWER_FIELDS = [
  "firstName",
  "lastName",
  "emailAddress",
  "phone",
  "phoneNumber",
  "citizenshipStatus",
  "maritalStatus",
  "dateOfBirth",
  "borrowerSSN",
  "borrowerHasSSN",
  "estimatedFICO",
  "primaryAddress",
];

export const submissionBorrower = (row = {}) => {
  const merged = { ...(row.borrowers?.[0] || {}) };
  BORROWER_FIELDS.forEach((k) => {
    if (row[k] !== undefined && row[k] !== null && row[k] !== "") {
      merged[k] = row[k];
    }
  });
  return merged;
};

export const submissionBorrowerName = (row = {}) => {
  const b = submissionBorrower(row);
  const name = [b.firstName, b.lastName].filter(Boolean).join(" ").trim();
  return name || null;
};

export const submissionEmail = (row = {}) =>
  submissionBorrower(row).emailAddress || null;

/**
 * The borrowing entity, however this submission happens to carry it.
 *
 * Exactly the same split as the borrower: the normalized shape is
 * `entities[0]` (ApplicationEntity — entityName, entityAddress,
 * entityStateOfOrganization, entityType, entityEINNumber), but live submissions
 * routinely arrive with that array EMPTY and the same fields inline at the top
 * level. Reading only one side is why Type, State of organization and EIN
 * rendered as em dashes on submissions that plainly had them.
 *
 * The application-level answers about the entity (does one already exist, what
 * share does the applicant own, does it hold assets) only ever live at the top
 * level — they describe the applicant s relationship to it, not the entity.
 */
const ENTITY_FIELDS = [
  "entityName",
  "entityAddress",
  "entityStateOfOrganization",
  "entityType",
  "entityEINNumber",
];

export const submissionEntity = (row = {}) => {
  const merged = { ...(row.entities?.[0] || {}) };
  ENTITY_FIELDS.forEach((k) => {
    if (row[k] !== undefined && row[k] !== null && row[k] !== "") {
      merged[k] = row[k];
    }
  });
  return merged;
};

export const submissionEntityName = (row = {}) =>
  submissionEntity(row).entityName || null;

export const submissionPropertyAddress = (row = {}) => {
  const property = row.subjectProperties?.[0];
  return (
    property?.address?.fullAddress || row.primaryAddress?.fullAddress || null
  );
};

export const submissionPhone = (row = {}) => {
  const b = submissionBorrower(row);
  return b.phone || b.phoneNumber || null;
};

/**
 * Who signed, per the attestation record. The field is `signed_name` — reading
 * `typed_name` (the plan's wording) silently falls through to the submitting
 * email address, which looks like data rather than a miss.
 */
export const submissionSignedBy = (row = {}) =>
  row.attestation?.signed_name || row.submitted_by_email || null;

/**
 * Uploaded files. `documents` is a list of ObjectId STRINGS — the file names,
 * doc groups and types live in `uploadMetadata`. Reading `documents` for display
 * yields a list of ids, so prefer the metadata and fall back to a count.
 */
export const submissionDocuments = (row = {}) =>
  Array.isArray(row.uploadMetadata) && row.uploadMetadata.length
    ? row.uploadMetadata
    : [];

export const submissionDocumentCount = (row = {}) =>
  submissionDocuments(row).length ||
  (Array.isArray(row.documents) ? row.documents.length : 0);

export const submissionIsAssigned = (row = {}) =>
  row.dealId != null || row.loanNumber != null;

/**
 * Where a submission's loan lives. The single place this route is spelled, so
 * the status chips (which link) and the detail panel (which navigates) can't
 * drift apart.
 *
 * LENDMARQ DIVERGENCE: tpo's loan drilldown is /loans/:loanId; this app's is
 * /loan/:id.
 */
export const loanDrilldownHref = (dealId) =>
  dealId ? `/loan/${dealId}` : null;

/** Submitted-at as an epoch ms, 0 when missing so undated rows sort last. */
export const submissionTime = (row = {}) =>
  row.submission_date ? new Date(row.submission_date).getTime() : 0;

/**
 * Newest first. The two kinds arrive as two separate lists, so without this any
 * combined view shows every application before the first credit authorization —
 * which reads as "the credit auths are missing" rather than "they're further
 * down".
 */
export const byNewestSubmission = (a, b) =>
  submissionTime(b) - submissionTime(a);

/**
 * Identity key used to roll a borrower's submissions together. Email is the only
 * field that is both reliably present and stable across separate submissions —
 * the same person filling out an application and a credit auth produces two
 * documents with different `_id`s and no shared borrower record.
 */
export const submissionBorrowerKey = (row = {}) => {
  const email = submissionEmail(row);
  if (email) return `email:${String(email).trim().toLowerCase()}`;
  const name = submissionBorrowerName(row);
  if (name) return `name:${name.toLowerCase()}`;
  return `id:${row._id}`;
};

/**
 * Who the submission is attributed to — the salesperson whose link it came
 * through, inherited from `application_links` at mint time. It's a user `_id`,
 * so it compares against `state.user._id`, not an email.
 */
export const submissionOwnerId = (row = {}) =>
  row.owner_user_id == null ? null : String(row.owner_user_id);

export const submissionIsMine = (row, userId) =>
  Boolean(userId) && submissionOwnerId(row) === String(userId);

/** "JO" for James Oliva — the avatar fallback when there's no photo. */
export const initialsOf = (name = "") =>
  String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

/**
 * How the borrower groups are ordered. Grouped rows can't use the column
 * headers — the headers are ordinary rows sitting above their children, so an
 * AG Grid re-sort would scatter them — which is why the order is chosen here
 * instead.
 */
export const GROUP_SORTS = {
  RECENT: "recent",
  NAME: "name",
};

export const GROUP_SORT_OPTIONS = [
  {
    value: GROUP_SORTS.RECENT,
    label: "Most recent",
    hint: "By each borrower's newest submission",
  },
  {
    value: GROUP_SORTS.NAME,
    label: "Borrower name",
    hint: "A to Z",
  },
];

/**
 * Group a submission list by borrower:
 *   [{ key, name, email, rows, apps, auths, onLoans, total, newestTime }]
 *
 * One row per borrower is NOT what this produces — the borrower becomes a header
 * with every one of their submissions listed beneath it, so nothing is hidden
 * behind a rollup the way collapsing to a carrier row did.
 *
 * `newestTime` is the group's sort key under GROUP_SORTS.RECENT: the newest
 * submission ACROSS ALL of that borrower's submissions, not the date on any one
 * carrier row. So a borrower whose oldest submission is an application and whose
 * newest is a credit auth sorts on the credit auth.
 *
 * A group's own rows always stay newest-first regardless of how the groups are
 * ordered — within one person, chronology is the only useful order.
 */
export const buildBorrowerGroups = (rows = [], { sortBy } = {}) => {
  const groups = new Map();

  rows.forEach((row) => {
    const key = submissionBorrowerKey(row);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        name: submissionBorrowerName(row) || "Unnamed borrower",
        email: submissionEmail(row),
        rows: [],
      });
    }
    groups.get(key).rows.push(row);
  });

  const built = [...groups.values()].map((g) => {
    const rowsSorted = [...g.rows].sort(byNewestSubmission);
    return {
      ...g,
      rows: rowsSorted,
      total: rowsSorted.length,
      apps: rowsSorted.filter((r) => r.__type === LANES.LOAN_APPLICATION).length,
      auths: rowsSorted.filter((r) => r.__type === LANES.CREDIT_AUTHORIZATION)
        .length,
      onLoans: rowsSorted.filter(submissionIsAssigned).length,
      // Rows are newest-first, so the head IS the max — no second pass needed.
      newestTime: submissionTime(rowsSorted[0]),
    };
  });

  if (sortBy === GROUP_SORTS.NAME) {
    // localeCompare so accented names sort where a reader expects, and
    // case-insensitively — "PETRONIO CORREA" shouldn't sort apart from "Petra".
    return built.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    );
  }
  return built.sort((a, b) => b.newestTime - a.newestTime);
};

/** "2 apps · 1 auth" — what a borrower has sent, in one line. */
export const groupKindSummary = ({ apps = 0, auths = 0 } = {}) =>
  [
    apps ? `${apps} app${apps > 1 ? "s" : ""}` : null,
    auths ? `${auths} auth${auths > 1 ? "s" : ""}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * A submission → the prefill payload for /send/:linkType, so a broker can send
 * this borrower another link without retyping what they already told us.
 *
 * Keys are the public forms' own field names (see sendToBorrowerConfig
 * `borrowerFields`). Extra keys are harmless — the send screen renders only the
 * fields its link kind offers and `buildPrefill` filters the rest before minting
 * — but only fields the borrower actually filled are worth carrying.
 */
export const submissionToSendPrefill = (row = {}) => {
  const borrower = submissionBorrower(row);
  const prefill = {
    firstName: borrower.firstName,
    lastName: borrower.lastName,
    emailAddress: submissionEmail(row),
    phone: submissionPhone(row),
    citizenshipStatus: borrower.citizenshipStatus,
    estimatedFICO: borrower.estimatedFICO,
    entityName: submissionEntityName(row),
    primaryAddress: borrower.primaryAddress,
  };
  return Object.fromEntries(
    Object.entries(prefill).filter(
      ([, v]) => v !== null && v !== undefined && v !== "",
    ),
  );
};

// The list endpoints strip SSN, but the detail read (application/{id}) returns it
// — mask it either way so a full SSN never renders.
export const maskSSN = (ssn) => {
  if (!ssn) return null;
  return `•••-••-${String(ssn).slice(-4)}`;
};

export const formatPercentValue = (n) => {
  if (n == null || n === "") return null;
  const pct = Number(n) * 100;
  if (!Number.isFinite(pct)) return null;
  return `${pct.toFixed(pct % 1 ? 1 : 0)}%`;
};

export const formatFileSize = (bytes) => {
  if (bytes == null) return "";
  const mb = bytes / 1048576;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

/**
 * Queue lanes. The lane lives in the URL (mirroring the lendmarq-los
 * /applications/:slug routes) so the Submissions nav dropdown deep-links into a
 * lane and a reload keeps it.
 *
 * `createParam` / `assignKind` are the two backend vocabularies — the routes
 * spell the same concept differently:
 *   POST /underwriting/create_deal_from_submission/{createParam}/{id}
 *   POST /underwriting/assign_to_deal/{assignKind}/{loanNumber}
 */
export const LANES = {
  ALL: "all",
  LOAN_APPLICATION: "loan_application",
  CREDIT_AUTHORIZATION: "credit_authorization",
};

// Short form for the toolbar pills — the long `label` names the thing, these
// name the filter.
export const LANE_PILL_LABELS = {
  [LANES.ALL]: "All",
  [LANES.LOAN_APPLICATION]: "Loan apps",
  [LANES.CREDIT_AUTHORIZATION]: "Credit auths",
};

/**
 * The assignment scope filter. Sub-labels because "Needs a loan" and "On a loan"
 * are the queue's own vocabulary, not something a new user can infer.
 */
export const SCOPES = {
  ALL: "all",
  UNASSIGNED: "unassigned",
  ASSIGNED: "assigned",
};

export const SCOPE_OPTIONS = [
  {
    value: SCOPES.ALL,
    label: "All submissions",
    hint: "Attached and unattached",
  },
  {
    value: SCOPES.UNASSIGNED,
    label: "Needs a loan",
    hint: "Not attached to any loan yet",
  },
  {
    value: SCOPES.ASSIGNED,
    label: "On a loan",
    hint: "Already attached to a loan",
  },
];

export const LANE_META = {
  [LANES.ALL]: {
    path: "/submissions",
    label: "Submission",
    description:
      "Applications and credit authorizations borrowers have signed through your links. Expand a row for the full submission, then attach it to a loan.",
    emptyTitle: "No submissions yet",
    emptyMessage:
      "Applications and credit authorizations will appear here as borrowers submit them.",
  },
  [LANES.LOAN_APPLICATION]: {
    path: "/submissions/applications",
    label: "Loan Application",
    description:
      "Applications borrowers have submitted through your links. Expand a row for the full submission, then attach it to an existing loan or create a new one.",
    emptyTitle: "No applications yet",
    emptyMessage:
      "Loan applications will appear here as borrowers submit them.",
    formPath: "/loan-application",
    formButtonLabel: "Open Loan App",
    createParam: "loanApplication",
    assignKind: "application",
  },
  [LANES.CREDIT_AUTHORIZATION]: {
    path: "/submissions/credit-auths",
    label: "Credit Authorization",
    description:
      "Credit authorizations borrowers have signed. Expand a row for the full submission, then attach it to an existing loan or create a new one.",
    emptyTitle: "No credit authorizations yet",
    emptyMessage:
      "Signed credit authorizations will appear here as borrowers complete them.",
    formPath: "/credit-auth",
    formButtonLabel: "Open Credit Auth",
    createParam: "creditAuthorization",
    assignKind: "creditAuth",
  },
};

// Status vocabulary from APPLICATION_STATUSES in app/models/loan_applications.py.
export const SUBMISSION_STATUSES = [
  "submitted",
  "under_review",
  "assigned",
  "returned",
  "resubmitted",
  "archived",
];

export const SUBMISSION_STATUS_LABELS = {
  submitted: "Submitted",
  under_review: "Under review",
  assigned: "Assigned",
  returned: "Returned",
  resubmitted: "Resubmitted",
  archived: "Archived",
};
